import { BffApiClientMolecule } from "@/app/api-client/store"
import type { OfferMatch } from "@/app/nft/sell/use-sell-state"
import { getBaseNftEventContext } from "@/app/nft/utils/get-base-nft-event-context"
import { WalletConnectMoleculeReatom } from "@/app/wallet-connect/store.reatom"
import { LoggerMolecule } from "@/instrumentation/logger/logger-molecule"
import { type EventContext, Flow } from "@/instrumentation/logger/types"
import * as amplitude from "@amplitude/analytics-browser"
import type { Blockchain } from "@anaconda/core/blockchain/types"
import type { NftMiniature } from "@anaconda/core/nft/types"
import type { ItemId } from "@rarible_int/anaconda_sdk/ethereum"
import {
  type AcceptCollectionBidRequest,
  acceptCollectionBidFactoryReatom,
} from "@rarible_int/anaconda_sdk/solana"
import { sonnerToast } from "@rarible_int/anaconda_ui_components"
import { type Atom, action, withAsync, withCallHook, wrap } from "@reatom/core"
import { molecule } from "bunshi"
import { SolanaSdkContainerMoleculeReatom } from "../../blockchains/solana-sdk-container-molecule.reatom"
import { AcceptCollectionBidToast } from "../../toasts/accept-collection-bid-toast"
import {
  TransactionEvent,
  getTransactionErrorMessage,
} from "../events/transaction-event"
import { getShownRoyaltyBps } from "./shown-royalty"

export interface AcceptCollectionBidActionParams {
  selectedNftsAtom: Atom<NftMiniature[]> | (() => NftMiniature[])
  offerByNftAtom:
    | Atom<Record<ItemId, OfferMatch>>
    | (() => Record<ItemId, OfferMatch>)
  blockchain: Blockchain
  capturedSelectedNfts?: NftMiniature[]
  capturedOfferByNft?: Record<ItemId, OfferMatch>
}

export interface AcceptCollectionBidActionResult {
  successfulTransactions: Array<{ itemId: ItemId; txId: string }>
  errorTransactions: Array<{ itemId: ItemId; error: Error }>
}

export const SolanaAcceptCollectionBidTransactionMoleculeReatom = molecule(
  (mol) => {
    const logger = mol(LoggerMolecule)
    const { sdkContainerAtom } = mol(SolanaSdkContainerMoleculeReatom)
    const { solanaAddress: solanaAddressAtom } = mol(
      WalletConnectMoleculeReatom,
    )
    const bffApiClient = mol(BffApiClientMolecule)

    const acceptCollectionBidActionFactory = () => {
      let acceptId: string | number | undefined

      const acceptCollectionBidAction = action(
        async ({
          selectedNftsAtom,
          offerByNftAtom,
          blockchain,
          capturedSelectedNfts,
          capturedOfferByNft,
        }: AcceptCollectionBidActionParams) => {
          const container = sdkContainerAtom()
          const from = solanaAddressAtom()

          if (!from) {
            throw new Error("Solana wallet address is not available")
          }

          const selectedNfts = capturedSelectedNfts ?? selectedNftsAtom()
          const offers = capturedOfferByNft ?? offerByNftAtom()

          if (selectedNfts.length === 0) {
            throw new Error("No NFTs selected")
          }

          const matchedNfts = selectedNfts.filter(
            (nft) => !!offers[nft.id]?.offer,
          )
          if (matchedNfts.length === 0) {
            throw new Error("No offers available for selected NFTs")
          }
          const selectedIds = matchedNfts.map((nft) => nft.id as ItemId)
          const ordersMap = matchedNfts.reduce<
            AcceptCollectionBidRequest["ordersMap"]
          >((acc, nft) => {
            const offer = offers[nft.id]
            if (offer?.offer) {
              acc[nft.id as ItemId] = offer.offer.order
            }
            return acc
          }, {})

          // An item whose royalty can't be read is left out of the map, and
          // the SDK reports it failed instead of filling it uncapped.
          const maxRoyaltyBpsByItemId: AcceptCollectionBidRequest["maxRoyaltyBpsByItemId"] =
            {}
          await wrap(
            Promise.all(
              matchedNfts.map(async (nft) => {
                try {
                  maxRoyaltyBpsByItemId[nft.id as ItemId] =
                    await getShownRoyaltyBps(bffApiClient, nft.id)
                } catch (error) {
                  logger?.api.pushError(
                    error instanceof Error ? error : new Error(String(error)),
                    { context: { itemId: nft.id, step: "royalty cap" } },
                  )
                }
              }),
            ),
          )

          const acceptCollectionBid =
            acceptCollectionBidFactoryReatom(container)
          const request: AcceptCollectionBidRequest = {
            itemIds: selectedIds,
            ordersMap,
            blockchain,
            from,
            maxRoyaltyBpsByItemId,
          }

          const result = await wrap(acceptCollectionBid(request))

          const successfulTransactions = result.successfulTransactions.map(
            ({ itemId, txId }) => ({ itemId, txId }),
          )
          const errorTransactions = result.errorTransactions.map(
            ({ itemId, error }) => ({ itemId, error }),
          )

          for (const transactionInfo of successfulTransactions) {
            const nft = selectedNfts.find(
              (item) => item.id === transactionInfo.itemId,
            )
            if (!nft) continue

            const eventContext = {
              flow: Flow.AcceptMultipleOffers,
              ...getBaseNftEventContext({
                id: nft.id,
                collection: nft.collection,
              }),
              transactionId: transactionInfo.txId,
            }
            logger?.api.pushEvent(TransactionEvent.Completed, eventContext)
            amplitude.track(TransactionEvent.Completed, eventContext)
            sonnerToast.custom((id) => (
              <AcceptCollectionBidToast.Success
                nft={nft}
                id={id}
                transactionId={transactionInfo.txId}
              />
            ))
          }

          for (const transactionInfo of errorTransactions) {
            const nft = selectedNfts.find(
              (item) => item.id === transactionInfo.itemId,
            )
            if (!nft) continue

            const eventContext = {
              flow: Flow.AcceptMultipleOffers,
              ...getBaseNftEventContext({
                id: nft.id,
                collection: nft.collection,
              }),
              error: getTransactionErrorMessage(transactionInfo.error),
            }
            logger?.api.pushEvent(TransactionEvent.Failed, eventContext)
            amplitude.track(TransactionEvent.Failed, eventContext)
            sonnerToast.custom((id) => (
              <AcceptCollectionBidToast.Error
                nft={nft}
                id={id}
                error={transactionInfo.error}
              />
            ))
          }

          if (acceptId !== undefined) {
            sonnerToast.dismiss(acceptId)
          }

          return {
            successfulTransactions,
            errorTransactions,
          } satisfies AcceptCollectionBidActionResult
        },
        "solanaAcceptCollectionBidAction",
      ).extend(
        withAsync(),
        withCallHook(async (values, [params]) => {
          const selectedNfts =
            params.capturedSelectedNfts ?? params.selectedNftsAtom()

          acceptId = sonnerToast.custom(
            (id) => (
              <AcceptCollectionBidToast.Pending nfts={selectedNfts} id={id} />
            ),
            {
              duration: Number.POSITIVE_INFINITY,
            },
          )

          const multipleTransactionEventContext: EventContext = {
            flow: Flow.AcceptMultipleOffers,
            blockchain: params.blockchain,
          }

          logger?.api.pushEvent(
            TransactionEvent.Started,
            multipleTransactionEventContext,
          )
          amplitude.track(
            TransactionEvent.Started,
            multipleTransactionEventContext,
          )
        }),
      )

      acceptCollectionBidAction.onReject.extend(
        withCallHook(({ error, params: [paramsValue] }) => {
          const selectedNfts =
            paramsValue.capturedSelectedNfts ?? paramsValue.selectedNftsAtom()
          const multipleTransactionEventContext: EventContext = {
            flow: Flow.AcceptMultipleOffers,
            blockchain: paramsValue.blockchain,
          }
          const context = {
            ...multipleTransactionEventContext,
            error: getTransactionErrorMessage(error),
          }

          logger?.api.pushEvent(TransactionEvent.Failed, context)
          amplitude.track(TransactionEvent.Failed, context)

          sonnerToast.dismiss()
          if (selectedNfts.length > 0) {
            sonnerToast.custom((id) => (
              <AcceptCollectionBidToast.Error
                nft={selectedNfts[0]}
                id={id}
                error={error}
              />
            ))
          }
        }),
      )

      return acceptCollectionBidAction
    }

    return { acceptCollectionBidActionFactory }
  },
)
